/* vue-kerlink version 3.0.3 */
import Duration from 'duration-js';

/**
 * Add additionnal functions to duration-js
 * https://github.com/icholy/Duration.js
 */

const millisecond = 1;
const second = 1000 * millisecond;
const minute = 60 * second;
const hour = 60 * minute;
const day = 24 * hour;
const week = 7 * day;

// Export duration object to an object whitout converting to a specific unit
// 6w5d4h3m2s1ms will return { weeks: 6, days: 5, hours: 4, minutes: 3, seconds: 2, milliseconds: 1 }
Duration.prototype.toObject = function () {
  const sign = this._milliseconds < 0 ? '-' : '';

  // milliseconds
  let milliseconds = Math.abs(this._milliseconds);

  // weeks
  const weeks = Math.floor(milliseconds / week);
  if (weeks !== 0) milliseconds -= week * weeks;

  // days
  const days = Math.floor(milliseconds / day);
  if (days !== 0) milliseconds -= day * days;

  // hours
  const hours = Math.floor(milliseconds / hour);
  if (hours !== 0) milliseconds -= hour * hours;

  // minutes
  const minutes = Math.floor(milliseconds / minute);
  if (minutes !== 0) milliseconds -= minute * minutes;

  // seconds
  const seconds = Math.floor(milliseconds / second);
  if (seconds !== 0) milliseconds -= second * seconds;

  return {
    weeks: Number(sign + weeks),
    days: Number(sign + days),
    hours: Number(sign + hours),
    minutes: Number(sign + minutes),
    seconds: Number(sign + seconds),
    milliseconds: Number(sign + milliseconds)
  }
};

// Display a humanized duration like moment.duration().humanize()
// https://momentjs.com/docs/#/durations/humanize/
// new Duration("1m").humanize()       // a minute
// new Duration("2m").humanize()       // 2 minutes
// new Duration("24h").humanize()      // a day
// new Duration("1m").humanize(true)   // in a minute
// new Duration("-1m").humanize(true)  // a minute ago
Duration.prototype.humanize = function (withSuffix) {
  const durObject = this.toObject();

  function suffix (str, number) {
    if (withSuffix) {
      if (number > 0) return 'in ' + str
      else return str + ' ago'
    }
    return str
  }

  if (durObject.weeks !== 0) {
    if ((durObject.weeks === 1) || durObject.weeks === -1) return suffix('a week', durObject.weeks)
    return suffix(Math.abs(durObject.weeks) + ' weeks', durObject.weeks)
  }

  if (durObject.days !== 0) {
    if ((durObject.days === 1) || durObject.days === -1) return suffix('a day', durObject.days)
    return suffix(Math.abs(durObject.days) + ' days', durObject.days)
  }

  if (durObject.hours !== 0) {
    if ((durObject.hours === 1) || durObject.hours === -1) return suffix('an hour', durObject.hours)
    return suffix(Math.abs(durObject.hours) + ' hours', durObject.hours)
  }

  if (durObject.minutes !== 0) {
    if ((durObject.minutes === 1) || durObject.minutes === -1) return suffix('a minute', durObject.minutes)
    return suffix(Math.abs(durObject.minutes) + ' minutes', durObject.minutes)
  }

  return suffix('a few seconds', durObject.seconds)
};

/**
 * Return the formatted string of a duration in milliseconds
 * Uses duration-js
 * https://github.com/icholy/Duration.js
 *
 * @param integer  duration in milliseconds
 * @param string  unit to convert the duration
 *
 * @return string/integer the formatted duration
 */

const duration = (duration, unit) => {
  if (duration !== 0 && !duration) return ''
  if (typeof duration === 'string') duration = Number(duration);

  // Unit set return a rounded duration with unit
  const dur = new Duration(duration);
  // Get total of hours (24+)
  const hours = Math.floor(dur.hours());
  // Get real value of minutes and seconds (max 60)
  const minutes = dur.toObject().minutes;
  const seconds = dur.toObject().seconds;
  const milliseconds = dur.toObject().milliseconds;
  let durationAs = 0;
  let formattedDuration = '';

  const plural = (duration, unit) => {
    return duration + ' ' + unit + (duration !== 1 ? 's' : '')
  };

  switch (unit) {
    case 'millisecond':
      durationAs = Math.round(dur.milliseconds());
      return plural(durationAs, unit)

    case 'second':
      durationAs = Math.round(dur.seconds());
      return plural(durationAs, unit)

    case 'minute':
      durationAs = Math.round(dur.minutes());
      return plural(durationAs, unit)

    case 'hour':
      durationAs = Math.round(dur.hours());
      return plural(durationAs, unit)

    case 'day':
      durationAs = Math.round(dur.days());
      return plural(durationAs, unit)

    case 'compact':
      // If < 1s, display as ms
      const durationAsMs = Math.round(dur.milliseconds());
      if (durationAsMs < 1000) return durationAsMs + 'ms'

      // Else display ms as 0.x seconds
      let secondsAndMs = seconds;

      // [h]h [m]m [s]s
      formattedDuration += hours ? hours + 'h' : '';
      if (formattedDuration && minutes) formattedDuration += ' ';
      formattedDuration += minutes ? minutes + 'm' : '';
      if (formattedDuration && seconds) formattedDuration += ' ';
      if (milliseconds) secondsAndMs = seconds + Number((milliseconds / 1000).toFixed(1));
      formattedDuration += secondsAndMs ? secondsAndMs + 's' : '';
      return formattedDuration

    case 'full':
      // [h] hours [m] minutes [s] seconds
      formattedDuration += hours ? plural(hours, 'hour') : '';
      if (formattedDuration && minutes) formattedDuration += ' ';
      formattedDuration += minutes ? plural(minutes, 'minute') : '';
      if (formattedDuration && seconds) formattedDuration += ' ';
      formattedDuration += seconds ? plural(seconds, 'second') : '';

      return formattedDuration

    case 'suffix':
      return dur.humanize(true)

    default:
      return dur.humanize()
  }
};

export default duration;
